import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useGalleries } from '../hooks/useGalleries';
import { PhotoStreamClient } from '../api/PhotoStreamClient';

interface GalleryFormProps {
  onSuccess?: (galleryId: string) => void;
}

const apiClient = new PhotoStreamClient(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
);

export const GalleryForm: React.FC<GalleryFormProps> = ({ onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const { createGallery } = useGalleries(apiClient);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    let isValid = true;
    
    if (!name.trim()) {
      setNameError('Gallery name is required');
      isValid = false;
    } else {
      setNameError('');
    }

    if (!description.trim()) {
      setDescriptionError('Description is required');
      isValid = false;
    } else {
      setDescriptionError('');
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await createGallery(name.trim(), description.trim());
      setSuccess(true);
      
      // Reset form
      setName('');
      setDescription('');
      
      if (onSuccess) {
        // Note: We don't have the gallery ID from createGallery, so we'll navigate to home
        setTimeout(() => onSuccess(''), 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setName('');
    setDescription('');
    setNameError('');
    setDescriptionError('');
    setError(null);
    setSuccess(false);
  };

  if (success) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="text-4xl">✅</div>
            <div>
              <h3 className="text-lg font-semibold text-green-800">
                Gallery Created Successfully!
              </h3>
              <p className="text-sm text-green-600">
                Your new gallery has been created and is ready for photos.
              </p>
            </div>
            <Button onClick={handleReset} variant="outline">
              Create Another Gallery
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New Gallery</CardTitle>
        <CardDescription>
          Add a new gallery to organize and share your photos
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gallery-name">Gallery Name</Label>
            <Input
              id="gallery-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter gallery name..."
              className={nameError ? 'border-destructive' : ''}
            />
            {nameError && (
              <p className="text-sm text-destructive">{nameError}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="gallery-description">Description</Label>
            <Textarea
              id="gallery-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter gallery description..."
              rows={3}
              className={descriptionError ? 'border-destructive' : ''}
            />
            {descriptionError && (
              <p className="text-sm text-destructive">{descriptionError}</p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          <div className="flex space-x-2">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Gallery'}
            </Button>
            <Button type="button" variant="outline" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
